import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Grid } from 'antd';
import { db } from 'firebase/firebase';
import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';

import BoxContainer from '@src/components/BoxContainer';

import BoardForm from '@src/packages/dashboard/components/BoardForm';
import DashboardGrid from '@src/packages/dashboard/components/DashboardGrid';
import DashboardHeader from '@src/packages/dashboard/components/DashboardHeader';
import MetricsDrawer from '@src/packages/dashboard/components/MetricsDrawer';
import {
  SlidePanelContext,
  SlidePanelProvider,
} from '@src/packages/dashboard/components/SlidePanel/SlidePanelContext';

import { errorNotification } from 'appRedux/actions';

import { get, set } from '../../../utils/storage';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useFormHandlers } from '../../hooks/useFormHandlers';
import { useLayoutManager } from '../../hooks/useLayoutManager';
import {
  GROUP_BY_DEFAULT,
  ONE_WEEK_BACK,
  RELATIVE_DATE_PER_DEFAULT,
  TODAY_DATE,
} from './components/ChartFilters/constants';
import { DashboardDrawers } from './components/DashboardDrawers';
import { LIST_TIMER_OPTIONS, METRICS_FILTER } from './constants';
import {
  buildChartFilterClearProps,
  buildChartFilterSetValueProps,
  buildInitMetricsFilterValues,
  chartFiltersToStoragePayload,
  filterValuesToChartFilters,
  filterValuesToChartFiltersUi,
  normalizeStoredChartFilters,
  resolveChartFiltersFromBoard,
} from './metricsFilterConfig';
import {
  EventsByScheduleProvider,
  FILTERS_PANEL_VALUES,
  useEventsByScheduleContext,
} from './contexts/EventsByScheduleContext';
import styles from './styles.module.less';
import SecondaryPanel, {
  SecondaryPanelRailPortalHost,
} from '@src/components/SecondaryPanel/SecondaryPanel';
import ChartFilters from './components/ChartFilters';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import SlidePanel from '@src/components/SlidePanel';
import DashboardMenu from './components/DashboardMenu';
import SelectRefreshTimer from '@src/components/SelectRefreshTimer';
import { useSingleFilter } from './hooks/useSingleFilter';
import ListViewportLayout from '@src/components/ListViewportLayout';

const { useBreakpoint } = Grid;

const EventsDashboardContent = () => {
  const tableElementsRef = useRef({});
  const formRefsMap = useRef({});

  const intl = useIntl();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const {
    boardSelected,
    onDeleteBoard,
    onOpenBoardFormEdit,
    isOpen: slidePanelOpened,
  } = useContext(SlidePanelContext);

  const {
    isPanelOpen,
    openFiltersPanel,
    generalFiltersValues,
    setGeneralFilterValue,
    panelSelected,
    renderFiltersContent,
    railPortalHostEl,
  } = useSingleFilter();

  const hasBoardSelected = !!boardSelected.id;
  const isMobile = screens.xs;
  const orgId = useSelector(state => state.organizations.organization.id);
  const storedMetricsSnapshot = useMemo(() => get(METRICS_FILTER), []);

  const resolvedInitialChart = useMemo(() => {
    const fromStorage = normalizeStoredChartFilters(storedMetricsSnapshot);
    if (fromStorage) return fromStorage;
    const fromBoard = resolveChartFiltersFromBoard(boardSelected);
    if (fromBoard) return fromBoard;
    return {
      relative: RELATIVE_DATE_PER_DEFAULT,
      dateRange: [ONE_WEEK_BACK, TODAY_DATE],
      groupBy: GROUP_BY_DEFAULT,
    };
  }, [boardSelected, storedMetricsSnapshot]);

  const initFilterValues = useMemo(
    () => buildInitMetricsFilterValues(resolvedInitialChart, intl),
    [resolvedInitialChart, intl],
  );

  const [loading, setLoading] = useState(false);
  const [creatingChart, setCreatingChart] = useState(false);
  const [savingLayoutChanges, setSavingLayoutChanges] = useState(false);
  const [refreshChart, setRefreshChart] = useState(false); // state to refresh each element into responsive grid
  const [metricsComponenteSelected, setMetricsComponenteSelected] = useState(null);
  const currentElementSelected = useRef({});

  const {
    registerRefreshTimer,
    refreshTimer,
    refreshTimerRef,
    loading: contextLoading,
  } = useEventsByScheduleContext();

  const {
    charts,
    tables,
    pieCharts,
    countCharts,
    eventTypes,
    counterCharts,
    componentsCollectionRef,
    barCharts,
    loading: dashboardLoading,
  } = useDashboardData({ orgId, boardSelected, refreshChart });

  const someChartTypeSelected = Object.values(currentElementSelected.current).some(
    val => val !== null,
  );

  const { layoutChanges, hasChanges, checkLayoutChange, setLayoutChanges } = useLayoutManager(
    charts,
    tables,
    pieCharts,
    countCharts,
    counterCharts,
    barCharts,
  );

  const resolvedFilters = useMemo(() => {
    if (!generalFiltersValues || Object.keys(generalFiltersValues).length === 0) {
      return resolvedInitialChart;
    }
    return filterValuesToChartFilters(generalFiltersValues);
  }, [generalFiltersValues, resolvedInitialChart]);

  const chartFiltersUi = useMemo(
    () => filterValuesToChartFiltersUi(generalFiltersValues, resolvedInitialChart),
    [generalFiltersValues, resolvedInitialChart],
  );

  const saveLayout = useCallback(async () => {
    const changesList = Object.values(layoutChanges);
    if (!hasChanges) {
      return;
    }

    const batch = writeBatch(db);
    changesList.forEach(({ id, x, y, w, h, origin }) => {
      batch.update(doc(db, `${componentsCollectionRef[origin]}/${id}`), {
        layout: { x, y, w, h },
        updatedAt: serverTimestamp(),
      });
    });
    setSavingLayoutChanges(true);
    try {
      await batch.commit();
    } catch (e) {
      dispatch(errorNotification(e.message || e));
    }
    setSavingLayoutChanges(false);
    setLayoutChanges({});
  }, [layoutChanges, hasChanges, componentsCollectionRef, dispatch, setLayoutChanges]);

  const onRefreshCharts = useCallback(
    (forceRefresh = false) => {
      if (forceRefresh) {
        refreshTimer.reset();
        setRefreshChart(!refreshChart);
      }
    },
    [refreshTimer, refreshChart],
  );

  const handleFilters = useCallback(
    e => {
      const key = e.key ?? e.name;
      const { value } = e;
      if (value === null || value === undefined) {
        setGeneralFilterValue(buildChartFilterClearProps(key));
        return;
      }
      setGeneralFilterValue(buildChartFilterSetValueProps(key, value, intl));
    },
    [intl, setGeneralFilterValue],
  );

  useEffect(() => {
    if (!generalFiltersValues || Object.keys(generalFiltersValues).length === 0) return;
    const chart = filterValuesToChartFilters(generalFiltersValues);
    set(METRICS_FILTER, chartFiltersToStoragePayload(chart));
  }, [generalFiltersValues]);

  const onCloseMainDrawer = () => {
    setCreatingChart(false);
    setMetricsComponenteSelected(null);
    currentElementSelected.current = {};
  };

  const { loading: formLoading, onFormFinish } = useFormHandlers({
    boardSelected,
    orgId,
    intl,
    onRefreshCharts,
    onCloseMainDrawer,
  });

  // NOTE: elemntId is the id of the element that is being edited and type is the type of the element (Charts, Tables, etc..)
  const onEdit = (elementId, type) => {
    setCreatingChart(true);
    setMetricsComponenteSelected(type);
    currentElementSelected.current[type] = elementId;
  };

  const handleDeleteBoard = useCallback(async () => {
    setLoading(true);
    await onDeleteBoard();
    setLoading(false);
  }, [onDeleteBoard]);

  const onCreateChart = useCallback(() => setCreatingChart(true), []);

  const pauseTime = hasChanges || someChartTypeSelected;
  const chartFiltersPanel = useMemo(
    () => (
      <ChartFilters
        handleFilters={handleFilters}
        onRefreshCharts={onRefreshCharts}
        filters={chartFiltersUi}
        pauseTime={pauseTime}
        selectRefreshTimerRef={refreshTimerRef}
      />
    ),
    [chartFiltersUi, handleFilters, onRefreshCharts, pauseTime, refreshTimerRef],
  );

  // Stable node: SecondaryPanel.Panel syncs `children` to context in an effect; a new inline element each render would loop updatePanel → setPanel.
  const secondaryPanelRailPortalHost = useMemo(
    () => <SecondaryPanelRailPortalHost key="events-dashboard-rail-portal-host" />,
    [],
  );

  const componentConfig = useMemo(
    () => ({
      formRefsMap,
      currentElementSelectedRef: currentElementSelected,
    }),
    [],
  );

  useEffect(() => {
    if (refreshTimerRef?.current) {
      registerRefreshTimer(refreshTimerRef.current);
    }
  }, [registerRefreshTimer, refreshTimerRef]);

  //Reset timer when the board selected changes
  useEffect(() => {
    if (boardSelected?.id) {
      refreshTimer.reset();
    }
  }, [boardSelected?.id, refreshTimer]);

  // Pause/Resume timer when creatingChart changes
  useEffect(() => {
    if (creatingChart) {
      refreshTimer.pause();
    } else {
      refreshTimer.resume();
    }
  }, [creatingChart, refreshTimer]);

  // Pause/Resume timer when widget filters panel is open
  useEffect(() => {
    const isWidgetFiltersPanelOpen = panelSelected === FILTERS_PANEL_VALUES.WIDGET && isPanelOpen;

    if (isWidgetFiltersPanelOpen) {
      refreshTimer.pause();
    } else {
      refreshTimer.resume();
    }
  }, [panelSelected, isPanelOpen, refreshTimer]);

  const widgetFiltersPortal =
    panelSelected === FILTERS_PANEL_VALUES.WIDGET &&
    isPanelOpen &&
    railPortalHostEl &&
    createPortal(renderFiltersContent, railPortalHostEl);

  return (
    <Form.Provider onFormFinish={onFormFinish}>
      {widgetFiltersPortal}
      <DashboardHeader
        boardSelected={boardSelected}
        filters={resolvedFilters}
        hasBoardSelected={hasBoardSelected}
        savingLayoutChanges={savingLayoutChanges}
        creatingChart={creatingChart}
        isMobile={isMobile || false}
        handleDeleteBoard={handleDeleteBoard}
        onOpenBoardFormEdit={onOpenBoardFormEdit}
        onCreateChart={onCreateChart}
        saveLayout={saveLayout}
        sideBarIsOpen={isPanelOpen}
        toggleSideBar={() => openFiltersPanel(FILTERS_PANEL_VALUES.SIDE_BAR)}
        hasChanges={hasChanges}
      />

      <FilterActiveTags.Container>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={() => openFiltersPanel(FILTERS_PANEL_VALUES.SIDE_BAR)} />
          </FilterActiveTags.ScrollArea>
          {!isMobile && (
            <FilterActiveTags.Trailing>
              <div className="gx-pb-2">
                <SelectRefreshTimer
                  pauseButton={true}
                  onRefresh={e => onRefreshCharts(e)}
                  addManualRefresh
                  manualButtonPos={isMobile ? 'right' : 'left'}
                  times={LIST_TIMER_OPTIONS}
                  initValue={1}
                  className={styles.refreshInput}
                  size={isMobile ? '100%' : 'auto'}
                  ref={refreshTimerRef}
                />
              </div>
            </FilterActiveTags.Trailing>
          )}
        </FilterActiveTags.Bar>
        {isMobile && (
          <div className="gx-pb-2 gx-pt-2">
            <SelectRefreshTimer
              pauseButton={true}
              onRefresh={e => onRefreshCharts(e)}
              addManualRefresh
              manualButtonPos={isMobile ? 'right' : 'left'}
              times={LIST_TIMER_OPTIONS}
              initValue={1}
              className={styles.refreshInput}
              size={isMobile ? '100%' : 'auto'}
              ref={refreshTimerRef}
            />
          </div>
        )}
      </FilterActiveTags.Container>
      <BoxContainer fullContent>
        <div className={classNames('gx-d-flex', styles.pageRow)}>
          <SlidePanel
            className="gx-mt-2"
            title={intl.formatMessage({ id: 'dashboards.metrics.boards.title' })}
          >
            <DashboardMenu />
          </SlidePanel>
          <BoxContainer
            className={slidePanelOpened && !isMobile}
            contentClassName={styles.content}
            loading={contextLoading}
          >
            <SecondaryPanel.Panel
              key="chart-general-filters"
              title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
              initialValues={initFilterValues}
            >
              {panelSelected === FILTERS_PANEL_VALUES.WIDGET
                ? secondaryPanelRailPortalHost
                : chartFiltersPanel}
            </SecondaryPanel.Panel>
            <SecondaryPanel.Content>
              <ListViewportLayout scrollable={false}>
                <DashboardGrid
                  charts={charts}
                  tables={tables}
                  pieCharts={pieCharts}
                  countCharts={countCharts}
                  counters={counterCharts}
                  barCharts={barCharts}
                  filters={resolvedFilters}
                  refreshChart={refreshChart}
                  eventTypes={eventTypes}
                  isMobile={isMobile}
                  loading={dashboardLoading}
                  formLoading={formLoading}
                  checkLayoutChange={checkLayoutChange}
                  onEdit={onEdit}
                  tableElementsRef={tableElementsRef}
                />
                <BoardForm filters={resolvedFilters} />
              </ListViewportLayout>
            </SecondaryPanel.Content>
            <MetricsDrawer
              open={creatingChart}
              loading={loading || formLoading}
              metricsComponenteSelected={metricsComponenteSelected}
              someChartTypeSelected={someChartTypeSelected}
              boardSelected={boardSelected}
              componentConfig={componentConfig}
              onClose={onCloseMainDrawer}
              setMetricsComponenteSelected={setMetricsComponenteSelected}
            />
          </BoxContainer>
        </div>
      </BoxContainer>
      <DashboardDrawers />
    </Form.Provider>
  );
};

const EventsDashboard = () => {
  return (
    <EventsByScheduleProvider>
      <SlidePanelProvider>
        <EventsDashboardContent />
      </SlidePanelProvider>
    </EventsByScheduleProvider>
  );
};

export default EventsDashboard;
